import { ProjectSub } from './../subscription/project.sub';
import { DataStore } from 'flux-store';
import { Observable, EMPTY } from 'rxjs';
import { Injectable } from '@angular/core';
import { CommandInterfaces, Command, Random, StateService, CommandResultError } from 'flux-core';
import { AbstractMessageCommand } from 'flux-connection';
import { get } from 'lodash';
import { ModelSubscriptionManager } from 'flux-subscription';
import { ProjectModel } from '../model/project.mdl';
import { CollaboratorType } from '../../collab/model/collaborator.mdl';
import { last } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

/**
 * This is the command to create a new project
 *
 * This extends {@link AbstractCommand} to send a request to the server.
 */
@Injectable()
@Command()
export class CreateProject extends AbstractMessageCommand {

    public static get dataDefinition(): {} {
        return {
            id: true,
            name: true,
            parentId: false,
            listUnderOrg: false,
            createdTime: true,
        };
    }

    public static get implements(): Array<CommandInterfaces> {
        return [
            'IMessageCommand',
            'IProjectCommand',
        ];
    }

    constructor( protected dataStore: DataStore,
                 protected translate: TranslateService,
                 protected modelSubManager: ModelSubscriptionManager,
                 protected state: StateService<any, any> ) {
    super()/* istanbul ignore next */;
    }

    public prepareData() {
        this.data = !this.data ? {} : this.data;
        const parentId = get( this.data, 'parentId', null );
        const hasListUnderOrg = Object.prototype.hasOwnProperty.call( this.data, 'listUnderOrg' );
        const listUnderOrg = get( this.data, 'listUnderOrg', false );

        this.data = {
            id: get( this.data, 'id', Random.projectId()),
            name: get( this.data, 'name', this.translate.instant( 'FOLDER_PANEL.UNTITLED_FOLDER' )),
            createdTime: Date.now(),
        };

        if ( parentId ) {
            this.data.parentId = parentId;
        }

        if ( parentId ) {
            if ( hasListUnderOrg ) {
                this.data.listUnderOrg = listUnderOrg;
            }
        } else {
            this.data.listUnderOrg = listUnderOrg;
        }
    }

    public execute(): Observable<any> {
        return this.dataStore.insert( ProjectModel, this.data );
    }

    public executeResult( response: any ): Observable<any> {
        this.modelSubManager.start( ProjectSub, this.data.id )
            .pipe( last())
            .subscribe(() => {
                this.state.set( 'FolderPanelDataChangeEvent', {
                    id: response.model.id,
                    type: 'project',
                    action: 'create',
                    payload: response.model,
                });
            });
        const projectMap = this.state.get( 'ProjectMap' );
        projectMap[ this.data.id ] = response.model;
        this.state.set( 'ProjectMap', projectMap );
        const folderPanelProjects = this.state.get( 'FolderPanelProjects' );
        if ( Array.isArray( folderPanelProjects )) {
            const exists = folderPanelProjects.find( p => p.id === response.model.id );
            if ( !exists ) {
                folderPanelProjects.push( response.model );
                this.state.set( 'FolderPanelProjects', folderPanelProjects );
            }
        }
        return EMPTY;
    }

    /**
     * This function will update the newly created project model with
     * current user as collaborator and execute 'executeResult' function.
     */
    public executeNetworkOffline(): Observable<any> {
        // NOTE: Since the project creation did not update in the server,
        // we are updating the collabs over here. This is required to filter
        // the projects in project-locator-> getProjects method.
        return this.dataStore.update( ProjectModel, { id: this.data.id },
            {
                $set: { collabs: [{ id: this.state.get( 'CurrentUser' ), role: CollaboratorType.OWNER }]},
            });
    }

    public revert(): Observable<any> {
        return this.dataStore.remove( ProjectModel, { id : this.data.id });
    }

    public onError( error: CommandResultError ) {
        if ( error.code &&
            ( error.code === 1624 || error.code === 1608 || error.code === 1626 )) {
            throw error;
        }
        /* istanbul ignore next */
        super.onError( error );
    }
}

Object.defineProperty( CreateProject, 'name', {
  value: 'CreateProject',
});
